import React, { useState } from "react";
import { Group, Text, AppShell, ActionIcon, Drawer, Badge, Avatar, Skeleton, Tooltip, Modal, Button, Stack, Divider, Paper } from "@mantine/core";
import { IconBell, IconUser, IconInfoCircle, IconMail, IconPhone, IconBuildingSkyscraper, IconBriefcase, IconChevronRight } from "@tabler/icons-react";
import NotificationDrawer from "../../../shared/components/ui/NotificationSystem/NotificationDrawer";
import { useUIEnhancements } from "../../../hooks/ui/useUIEnhancements";
import { useSendNotification } from "../../../hooks/notifications/useSendNotification.jsx";
import { useAuth } from "../../../hooks/core/useAuth";
import BackButton from "../../../shared/components/BackButton";

const Header = () => {
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const { isOnline } = useUIEnhancements();
  const { notifications } = useSendNotification();
  const { user, staffProfile, isLoading } = useAuth();

  // Get user information with proper fallbacks
  const userName = staffProfile?.displayName || user?.email?.split('@')[0] || "User";
  const userRole = staffProfile?.role || "Staff";
  const userEmail = staffProfile?.email || user?.email || "No email available";
  
  // Determine role badge color
  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "red";
      case "manager":
        return "indigo";
      case "staff":
        return "blue";
      default:
        return "gray";
    }
  };

  return (
    <AppShell.Header height={60} style={{ zIndex: 2000 }}>
      <Group justify="space-between" h="100%" px="md">
        <Group>
          <BackButton />
          <Text size="xl" weight={700}>
            One Portal - Zippe
          </Text>
        </Group>
        <Group>
          <ActionIcon variant="outline" onClick={() => setIsNotificationDrawerOpen(true)} pos="relative">
            <IconBell size={20} />
            {notifications && notifications.length > 0 && (
              <Badge size="xs" variant="filled" color="red" style={{ position: "absolute", top: -5, right: -5 }}>
                {notifications.length}
              </Badge>
            )}
          </ActionIcon>
          <Group spacing="xs" style={{ cursor: 'pointer' }} onClick={() => !isLoading && setIsProfileModalOpen(true)}>
            {isLoading ? (
              <>
                <Skeleton height={40} circle />
                <div>
                  <Skeleton height={12} width={80} mb={6} />
                  <Skeleton height={8} width={60} />
                </div>
              </>
            ) : (
              <>
                <Tooltip label={staffProfile ? `${staffProfile.department || 'No department'} • ${staffProfile.jobTitle || 'No job title'}` : 'User information not available'}>
                  {staffProfile?.photoURL ? (
                    <Avatar src={staffProfile.photoURL} alt={userName} radius="xl" size="md" 
                      styles={{
                        root: {
                          border: '2px solid var(--mantine-color-blue-5)',
                          boxShadow: '0 0 10px rgba(0,0,0,0.1)'
                        }
                      }}
                    />
                  ) : (
                    <Avatar radius="xl" size="md" color="blue"
                      styles={{
                        root: {
                          border: '2px solid var(--mantine-color-blue-5)',
                          boxShadow: '0 0 10px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      {userName.charAt(0).toUpperCase() || <IconUser size={24} />}
                    </Avatar>
                  )}
                </Tooltip>
                <div>
                  <Text size="sm" weight={500}>
                    {userName}
                  </Text>
                  <Text size="xs" color="dimmed">
                    {userRole} {staffProfile?.department ? `• ${staffProfile.department}` : ''}
                  </Text>
                </div>
                <IconChevronRight size={16} opacity={0.5} />
              </>
            )}
          </Group>
        </Group>
      </Group>
      {!isOnline && (
        <Text bg="red" c="white" ta="center" py={8}>
          You are currently offline. Some features may be unavailable.
        </Text>
      )}
      <Drawer opened={isNotificationDrawerOpen} onClose={() => setIsNotificationDrawerOpen(false)} title="Notifications" position="right">
        <NotificationDrawer onClose={() => setIsNotificationDrawerOpen(false)} />
      </Drawer>

      {/* User Profile Modal */}
      <Modal
        opened={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        title={<Text size="lg" weight={700}>User Profile</Text>}
        centered
        size="md"
      >
        <Stack spacing="md">
          <Group position="center" spacing="md" pb="md">
            {staffProfile?.photoURL ? (
              <Avatar 
                src={staffProfile.photoURL} 
                alt={userName} 
                radius="xl" 
                size={100}
                styles={{
                  root: {
                    border: '2px solid var(--mantine-color-blue-5)',
                    boxShadow: '0 0 10px rgba(0,0,0,0.1)'
                  }
                }}
              />
            ) : (
              <Avatar 
                radius="xl" 
                size={100} 
                color="blue"
                styles={{
                  root: {
                    border: '2px solid var(--mantine-color-blue-5)',
                    boxShadow: '0 0 10px rgba(0,0,0,0.1)'
                  }
                }}
              >
                {userName.charAt(0).toUpperCase() || <IconUser size={60} />}
              </Avatar>
            )}
            <div>
              <Text size="xl" weight={700}>
                {userName}
              </Text>
              <Badge size="lg" color={getRoleBadgeColor(userRole)} my={5}>
                {userRole}
              </Badge>
              {staffProfile?.department && (
                <Text size="sm" color="dimmed">
                  {staffProfile.department} {staffProfile?.jobTitle ? `• ${staffProfile.jobTitle}` : ''}
                </Text>
              )}
            </div>
          </Group>

          <Divider />

          <Stack spacing="xs">
            <Paper p="sm" withBorder>
              <Group spacing="xs">
                <IconMail size={16} color="gray" />
                <Text size="sm" weight={600}>Email</Text>
              </Group>
              <Text pl={24}>{userEmail}</Text>
            </Paper>

            {staffProfile?.phoneNumber && (
              <Paper p="sm" withBorder>
                <Group spacing="xs">
                  <IconPhone size={16} color="gray" />
                  <Text size="sm" weight={600}>Phone</Text>
                </Group>
                <Text pl={24}>{staffProfile.phoneNumber}</Text>
              </Paper>
            )}

            {staffProfile?.department && (
              <Paper p="sm" withBorder>
                <Group spacing="xs">
                  <IconBuildingSkyscraper size={16} color="gray" />
                  <Text size="sm" weight={600}>Department</Text>
                </Group>
                <Text pl={24}>{staffProfile.department}</Text>
              </Paper>
            )}

            {staffProfile?.jobTitle && (
              <Paper p="sm" withBorder>
                <Group spacing="xs">
                  <IconBriefcase size={16} color="gray" />
                  <Text size="sm" weight={600}>Job Title</Text>
                </Group>
                <Text pl={24}>{staffProfile.jobTitle}</Text>
              </Paper>
            )}
          </Stack>

          <Divider />

          <Group position="apart">
            <Button 
              component="a" 
              href="/staff/profile" 
              variant="outline" 
              leftIcon={<IconInfoCircle size={16} />}
            >
              View Full Profile
            </Button>
            <Button onClick={() => setIsProfileModalOpen(false)}>Close</Button>
          </Group>
        </Stack>
      </Modal>
    </AppShell.Header>
  );
};

export default Header;
